import * as XLSX from "xlsx";
import moment from "moment";

/**
 * Excel export.
 *
 * Reports are opened in Excel, filtered and forwarded, so a real .xlsx beats a
 * CSV: column widths survive, numbers stay numbers instead of being re-guessed
 * on open, and the header row can be frozen.
 *
 * Two entry points, because the reports produce data in two shapes:
 *  - exportRowsToXlsx  for screens that build their own rows in the browser
 *  - exportCsvToXlsx   for screens whose data arrives as CSV text from the API,
 *                      which is converted client-side so no endpoint changes
 */

/**
 * Promotes a plain number held as text to a real number, so Excel can total a
 * column without the "number stored as text" warning.
 *
 * Deliberately conservative: anything with a leading zero stays text, because
 * employee codes and card ids must not lose it, and only bare integers and
 * decimals qualify -- never something that could be a date or an id.
 */
const toNumberIfSafe = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!/^-?(0|[1-9]\d*)(\.\d+)?$/.test(trimmed)) return value;
  const asNumber = Number(trimmed);
  return Number.isFinite(asNumber) ? asNumber : value;
};

/** Excel rejects : \ / ? * [ ] in sheet names, and caps them at 31 characters. */
const safeSheetName = (name) =>
  (name || "Report").replace(/[:\\/?*[\]]/g, " ").trim().slice(0, 31) || "Report";

/** Timestamped so successive downloads do not overwrite each other. */
const stampedFileName = (fileName) =>
  `${(fileName || "Report").replace(/[^\w\-. ]/g, "_")}_${moment().format("YYYYMMDD_HHmm")}.xlsx`;

/**
 * Sizes each column to its widest cell, within sensible bounds -- without this
 * every column opens at Excel's default width and long names are cut off.
 */
const fitColumns = (sheet, rows) => {
  if (!rows.length) return;
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const widths = [];
  for (let column = 0; column < columnCount; column += 1) {
    let widest = 10;
    rows.forEach((row) => {
      const cell = row[column];
      if (cell === null || cell === undefined) return;
      widest = Math.max(widest, String(cell).length + 2);
    });
    widths.push({ wch: Math.min(widest, 48) });
  }
  sheet["!cols"] = widths;
};

/** Keeps the header row visible when a long report is scrolled. */
const freezeHeader = (sheet) => {
  sheet["!freeze"] = { xSplit: "0", ySplit: "1", topLeftCell: "A2", activePane: "bottomLeft" };
};

/**
 * @param rows      array of arrays; the first row is treated as the header
 * @param fileName  base name, timestamped and given the .xlsx extension
 * @param sheetName tab name inside the workbook
 */
export const exportRowsToXlsx = (rows, fileName, sheetName) => {
  if (!rows || !rows.length) return false;
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  fitColumns(sheet, rows);
  freezeHeader(sheet);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, safeSheetName(sheetName || fileName));
  XLSX.writeFile(book, stampedFileName(fileName));
  return true;
};

/**
 * Converts the API's CSV response into a workbook. SheetJS does the parsing, so
 * quoted fields and embedded commas are handled properly rather than by a
 * hand-rolled split.
 *
 * Accepts a Blob as well as a string: the POST export endpoints are called with
 * responseType "blob", so they hand back a Blob rather than text. Stringifying
 * one yields "[object Blob]", so it has to be read rather than coerced -- which
 * is why this returns a promise.
 */
export const exportCsvToXlsx = async (csvData, fileName, sheetName) => {
  if (!csvData) return false;

  let text;
  if (typeof csvData === "string") {
    text = csvData;
  } else if (typeof Blob !== "undefined" && csvData instanceof Blob) {
    text = await csvData.text();
  } else {
    text = String(csvData);
  }
  if (!text.trim()) return false;

  // raw:true keeps every value exactly as the API sent it.
  //
  // The alternatives both corrupt timestamps. Letting SheetJS coerce (raw:false)
  // turned "2026-09-09 22:33:42.647" into the serial 46274.94, which opens in
  // Excel as a bare number. cellDates:true is worse: it reads the string in the
  // browser's local zone and re-emits it in UTC, so the same export shows
  // different times depending on who downloads it. The API returns site-local
  // times, so they are passed through untouched.
  const parsed = XLSX.read(text, { type: "string", raw: true });
  const firstSheet = parsed.Sheets[parsed.SheetNames[0]];
  if (!firstSheet) return false;

  const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, blankrows: false });
  return exportRowsToXlsx(rows.map((row) => row.map(toNumberIfSafe)), fileName, sheetName);
};

/**
 * Writes several tabs into one workbook -- used by the monthly attendance
 * export, which ships the day-by-day grid alongside a per-employee summary.
 *
 * @param sheets array of { name, rows }
 */
export const exportSheetsToXlsx = (sheets, fileName) => {
  const usable = (sheets || []).filter((sheet) => sheet.rows && sheet.rows.length);
  if (!usable.length) return false;
  const book = XLSX.utils.book_new();
  usable.forEach((sheet) => {
    const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows);
    fitColumns(worksheet, sheet.rows);
    freezeHeader(worksheet);
    XLSX.utils.book_append_sheet(book, worksheet, safeSheetName(sheet.name));
  });
  XLSX.writeFile(book, stampedFileName(fileName));
  return true;
};
